import { useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import {
  conversationQuery,
  forgetUnsent,
  isSending,
  isUnsent,
  useSendMessage,
  useUnsentMessages,
  type OutgoingMessage,
} from '@/entities/conversation'
import { newIdempotencyKey, type MessageChannel } from '@/shared/api'
import { toast } from '@/shared/lib/toast'
import { isOutboxShown } from './outbox-shown'
import { keyExpired, retryable, unsentStaleToast, unsentSubject, unsentToast } from './unsent'

export type MessageSend = ReturnType<typeof useMessageSend>

/**
 * Композер и очередь отправки одного треда.
 *
 * ⚠️ ОЧЕРЕДЬ — ПОПЫТКИ ОТПРАВКИ В КЕШЕ МУТАЦИЙ (`useUnsentMessages`), А НЕ СОСТОЯНИЕ ЭКРАНА: экран
 * уходит, а запрос летит дальше, и упавшее без экрана сообщение ждёт в своей переписке — в «Чатах»
 * и в модалке брони одинаково. Выход сносит его вместе с кешем.
 *
 * ⚠️ Ключ идемпотентности мнётся один раз, на «Отправить», и уходит с каждым «Повторить»: новый
 * ключ на повторе — второе сообщение живому гостю (`shared/api/idempotency.ts`).
 *
 * ⚠️ Отказ говорит строка под пузырём, а тост — только когда её не видно (`outbox-shown.ts`).
 * `mutateAsync` реджектится — вызов без `catch` уводит на экран смерти.
 */
export function useMessageSend(conversationId: string) {
  const queryClient = useQueryClient()
  const [text, setText] = useState('')
  const inputRef = useRef<HTMLTextAreaElement>(null)

  const { mutateAsync } = useSendMessage(conversationId)
  const queue = useUnsentMessages(conversationId)
  // Час, по которому очередь судит о сутках ключа, — состояние, а не `Date.now()` в отрисовке:
  // «Повторить», нарисованный до суток, на нажатии его обновляет, и кнопки перерисовываются.
  const [now, setNow] = useState(() => Date.now())

  /*
   * ⚠️ Хук обязан быть верным и без ключа на родителе: сменись `conversationId` без размонтирования,
   * набранный текст уехал бы к другому гостю. Очередь переключается сама — она своя у каждой
   * переписки.
   *
   * Правка состояния прямо в рендере — штатный приём React: рендер перезапускается до покраски, и
   * лишнего кадра со старым текстом не будет.
   */
  const [ownerId, setOwnerId] = useState(conversationId)
  if (ownerId !== conversationId) {
    setOwnerId(conversationId)
    setText('')
  }

  /** Кому сообщение — снимается на нажатии: к отказу шапки может уже не быть (выход, смена компании). */
  const subjectOf = (value: string): string => {
    const conversation = queryClient.getQueryData(conversationQuery(conversationId).queryKey)
    const guest = conversation && { name: conversation.guestName, contact: conversation.contact }
    return unsentSubject(guest, value)
  }

  /**
   * Одна попытка. Её исход держит кеш мутаций; здесь — только кому о нём сказать. `earlier` —
   * отказы прошлых попыток: по ним тост знает, могло ли сообщение уже дойти.
   */
  const attempt = async (
    message: OutgoingMessage,
    subject: string,
    earlier: readonly unknown[] = [],
  ): Promise<void> => {
    try {
      await mutateAsync(message)
    } catch (error) {
      // Ловим ОБЯЗАТЕЛЬНО: без этого unhandled rejection уводит всё приложение на экран смерти.
      // Попытки стирают успех, «Убрать» и «Править» (когда ни одна не летит) и выход с кешем —
      // значит, пропасть к отказу летящей попытки они могли только от выхода.
      const waits = isUnsent(queryClient, message.idempotencyKey)
      if (waits && isOutboxShown(queryClient, conversationId, message.idempotencyKey)) return
      toast.error(error, unsentToast([...earlier, error], subject, waits))
    }
  }

  /** Сообщение, по которому можно действовать: упало, и ни одна его попытка не летит. */
  const failed = (idempotencyKey: string) => {
    const message = queue.find((unsent) => unsent.idempotencyKey === idempotencyKey)
    // Снимок рендера мог не успеть показать, что повтор уже летит, — спрашиваем кеш.
    if (!message || message.status !== 'failed' || isSending(queryClient, idempotencyKey)) return null
    return message
  }

  const send = (channelKind?: MessageChannel['kind']): void => {
    const value = text.trim()
    // Пустое не отправляем — так же устроен и сам макет (`doSend`, `prototype/Prototype.dc.html:6208`).
    if (!value) return

    setText('')
    void attempt({ text: value, idempotencyKey: newIdempotencyKey(), channelKind }, subjectOf(value))
  }

  /**
   * Положить текст в композер и отдать ему фокус — «Править» у карточки черновика ИИ
   * (`features/ai-draft-review`) и у отказа в очереди. Черновик редактировать нельзя (контракт),
   * поэтому «править» его значит написать своё сообщение, взяв текст черновика за основу.
   *
   * ⚠️ ДОПИСЫВАЕТ к набранному, а не затирает: запись `value` в поле обнуляет его историю правки, и
   * затёртое не вернёт даже Ctrl+Z.
   */
  const edit = (value: string): void => {
    // Уже лежит в поле целым абзацем — второй раз не кладём: поле растёт лишь до 110px, и копия ушла
    // бы гостю вместе с правкой. Кусок чужого слова — не «уже лежит»: «Да» в «Давайте» пропало бы.
    // Пустое — только фокус: так повтор «Править» черновика ИИ возвращает в поле.
    const present = !value.trim() || `\n\n${text.trim()}\n\n`.includes(`\n\n${value.trim()}\n\n`)
    const next = present ? text : text.trim() ? `${text.trimEnd()}\n\n${value}` : value
    setText(next)

    const input = inputRef.current
    if (!input) return
    /*
     * Значение приедет в поле следующим рендером, а каретку надо поставить в КОНЕЦ уже сейчас —
     * иначе человек начнёт править с начала строки. Пишем значение в DOM сами: React на ближайшем
     * рендере увидит в поле ровно то, что собирался положить, и трогать его не станет.
     *
     * Проверено зондом, а не выведено: «каретка на позиции 158 из 158, поле в фокусе».
     */
    input.value = next
    input.focus()
    input.setSelectionRange(next.length, next.length)
  }

  /** «Повторить» — тем же ключом, пока он решает исход (`retryable`). */
  const retry = (idempotencyKey: string): void => {
    const message = failed(idempotencyKey)
    if (!message) return
    if (!retryable(message)) {
      // Кнопка осталась с прошлой отрисовки, а сутки ключа прошли: сказать и перерисовать с «Править».
      if (keyExpired(message, Date.now())) {
        toast.error(message.error, unsentStaleToast(subjectOf(message.text)))
        setNow(Date.now())
      }
      return
    }
    // Кнопка уходит вместе со строкой отказа — фокус не должен упасть на страницу.
    inputRef.current?.focus()
    // ТА ЖЕ попытка: тот же ключ, текст и канал. Новый ключ = второе сообщение живому гостю.
    void attempt(message, subjectOf(message.text), message.failures)
  }

  /** «Править» — где повторить тем же ключом нельзя: текст уходит в поле под новый ключ. */
  const rewrite = (idempotencyKey: string): void => {
    const message = failed(idempotencyKey)
    if (!message || retryable(message)) return
    forgetUnsent(queryClient, idempotencyKey)
    edit(message.text)
  }

  const dismiss = (idempotencyKey: string): void => {
    if (!failed(idempotencyKey)) return
    forgetUnsent(queryClient, idempotencyKey)
    inputRef.current?.focus()
  }

  return { conversationId, text, setText, queue, now, send, retry, rewrite, dismiss, edit, inputRef }
}
